import axios from 'axios';
import delay from '../lib/delay';
import store from '../store';
import { getBlackbox } from '@arena/toolkit/src/plugins/iovation';

let pendingCount = 0,
	getParam = new URLSearchParams(window.location.search),
	getApiVersion = getParam.get('checkout_version'),
	currentApiVersion;

if (getApiVersion) {
	currentApiVersion = `/${getApiVersion}`;
} else {
	currentApiVersion = '';
}

const checkoutApiBaseURL = '/checkout-api',
	v1ApiCheckoutBaseURL = '/api/checkout/v1',
	verifyCouponToken = (token) =>
		axios.get(`${checkoutApiBaseURL}${currentApiVersion}/coupon/token`, { params: { p: token } }),
	redeemCoupon = (couponCode, checkoutId, language) =>
		axios.post(`${checkoutApiBaseURL}${currentApiVersion}/coupon?p=${checkoutId}&language=${language}`, {
			CouponCode: couponCode,
		}),
	getBillingInfoVerifyToken = (token, serviceType) =>
		axios.get(`${v1ApiCheckoutBaseURL}/billing-info`, {
			params: {
				token: token,
				...(serviceType && { serviceType: serviceType }),
			},
		}),
	getAuthorizedUrlEpin = (token) => axios.post(`${v1ApiCheckoutBaseURL}/transactions/methods/epin?token=${token}`),
	saveBillingLocation = (countryCode, zipCode) =>
		axios.put(`${v1ApiCheckoutBaseURL}/billing-info?checkoutId=${store.state.checkoutIdSession}`, {
			billingLocation: {
				countryCode: countryCode,
				zipCode: zipCode ? zipCode : '',
			},
		}),
	getPaymentMethods = (serviceType) =>
		axios.get(`${v1ApiCheckoutBaseURL}/methods`, {
			params: {
				checkoutId: store.state.checkoutIdSession,
				...(serviceType && { serviceType: serviceType }),
			},
		}),
	beginPayment = async (channelNo, otpCode = null) => {
		async function waitForBlackbox(retriesLeft = 10, interval = 300) {
			const { blackbox, finished } = await getBlackbox({ uri_hook: '/iojs' });
			if (finished) {
				return blackbox;
			} else if (retriesLeft) {
				await new Promise((r) => setTimeout(r, interval));
				return waitForBlackbox(retriesLeft - 1, interval);
			} else return blackbox;
		}
		return waitForBlackbox().then((blackbox) =>
			axios.post(`${v1ApiCheckoutBaseURL}/transactions?checkoutId=${store.state.checkoutIdSession}`, {
				channelNo: channelNo,
				blackbox: blackbox,
				...(otpCode && {
					otpCode: otpCode, // only for minor account the otpCode is required to be provided from the verify pin modal
				}),
			})
		);
	},
	getTransactionSuccessResult = (checkoutId) =>
		axios
			.get(`${v1ApiCheckoutBaseURL}/transactions?checkoutId=${checkoutId}`)
			.then((response) => {
				if (response.data.status === 'pending' && pendingCount < 10) {
					pendingCount += 1;
					return delay(1000).then(() => getTransactionSuccessResult(checkoutId));
				}
				if (response.data.account && response.data.account.email) {
					store.commit('setUserEmail', response.data.account.email);
				}
				if (response.data.account && response.data.account.balance) {
					store.commit('setAccountBalance', response.data.account.balance);
				}
				return response;
			})
			.catch((error) => {
				throw error;
			}),
	getTransactionTypeResult = (checkoutId, type) =>
		axios
			.get(`${v1ApiCheckoutBaseURL}/transactions/${type}?checkoutId=${checkoutId}`)
			.then((response) => {
				if (response.data.account && response.data.account.email) {
					store.commit('setUserEmail', response.data.account.email);
				}
				if (response.data.account && response.data.account.balance) {
					store.commit('setAccountBalance', response.data.account.balance);
				}
				return response;
			})
			.catch((error) => {
				throw error;
			}),
	// ingame revamp:
	verifyInGameToken = (token, serviceType) =>
		axios.get(`${v1ApiCheckoutBaseURL}/microtransactions/order?token=${token}&serviceType=${serviceType}`, {
			headers: {
				'Cache-Control': 'no-cache',
				Pragma: 'no-cache',
				Expires: '0',
			},
		}),
	createCheckoutInGameToken = (checkoutId, languageCode = 'en', productId, mileageAmount) =>
		axios.post(`${v1ApiCheckoutBaseURL}/microtransactions/checkout?checkoutId=${checkoutId}`, {
			languageCode: languageCode,
			...(productId && { productId: productId }),
			...(mileageAmount && { mileageAmount: Number(mileageAmount) }),
		}),
	completeInGameTransaction = (checkoutId) =>
		axios
			.put(`${v1ApiCheckoutBaseURL}/microtransactions?checkoutId=${checkoutId}`)
			.then((response) => {
				store.commit('setInGameTransactionResult', response.data);
				if (response.data.account && response.data.account.balance)
					store.commit('setAccountBalance', response.data.account.balance);
				if (response.data.account && response.data.account.email)
					store.commit('setUserEmail', response.data.account.email);

				return response;
			})
			.catch((error) => {
				throw error;
			}),
	getInGameTransactionResult = (checkoutId) =>
		axios
			.get(`${v1ApiCheckoutBaseURL}/microtransactions?checkoutId=${checkoutId}`)
			.then((response) => {
				if (response.data.status === 'pending' && pendingCount < 10) {
					pendingCount += 1;
					return delay(1000).then(() => getInGameTransactionResult(checkoutId));
				}
				store.commit('setInGameTransactionResult', response.data);
				if (response.data.account && response.data.account.balance)
					store.commit('setAccountBalance', response.data.account.balance);
				if (response.data.account && response.data.account.email)
					store.commit('setUserEmail', response.data.account.email);

				return response;
			})
			.catch((error) => {
				throw error;
			}),
	cancelInGameTransaction = (checkoutId) =>
		axios.delete(`${v1ApiCheckoutBaseURL}/microtransactions?checkoutId=${checkoutId}`),
	getInGameTransactionTypeResult = (checkoutId, type) =>
		axios
			.get(`${v1ApiCheckoutBaseURL}/microtransactions/${type}?checkoutId=${checkoutId}`)
			.then((response) => {
				store.commit('setInGameTransactionResult', response.data);

				return response;
			})
			.catch((error) => {
				throw error;
			}),
	generateOtpCode = () =>
		axios.post(`${v1ApiCheckoutBaseURL}/generate-otp?checkoutId=${store.state.checkoutIdSession}`, {
			channelNo: store.state.selectedPg.channelNo,
		}),
	getMileageBalance = () => {
		return axios.get(`${v1ApiCheckoutBaseURL}/mileage`, {
			params: {
				checkoutId: store.state.inGameOrderData.checkoutId,
			},
		});
	},
	checkMileageUsage = (amount) =>
		axios.post(`${v1ApiCheckoutBaseURL}/mileage?checkoutId=${store.state.inGameOrderData.checkoutId}`, {
			mileageAmount: Number(amount),
		}),
	CheckoutAPI = {
		install(Vue) {
			Vue.prototype.$checkoutAPI = {
				verifyCouponToken,
				redeemCoupon,
				getBillingInfoVerifyToken,
				getAuthorizedUrlEpin,
				saveBillingLocation,
				getPaymentMethods,
				beginPayment,
				getTransactionSuccessResult,
				getTransactionTypeResult,
				verifyInGameToken,
				createCheckoutInGameToken,
				completeInGameTransaction,
				getInGameTransactionResult,
				cancelInGameTransaction,
				getInGameTransactionTypeResult,
				generateOtpCode,
				getMileageBalance,
				checkMileageUsage,
			};
		},
	};

export default CheckoutAPI;

export { verifyCouponToken, getBillingInfoVerifyToken, getAuthorizedUrlEpin, verifyInGameToken, generateOtpCode };
